import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { field, pills, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { setupBar, setupQuery, type Picker } from "../../ui/setup-bar";

// Settings › Intake questions (brief 1C-1, mockup settings/intake): the questions intake asks,
// from the one question bank. Each says which client types ask it, when, and where its answer is
// saved (on the patient or the episode). Each type's intake form follows the 1B-7 version rules:
// a draft changes in place, Approve locks it, and a change to an approved form makes the next
// version. Intake asks the latest approved version; a referral keeps the version it started on.

interface Question {
  id: string;
  key: string;
  label: string;
  answerType: string;
  answerLabel: string;
  savedOn: string;
  savedOnLabel: string;
  options: string[];
  askedOf: string[];
  archived: boolean;
}
interface FormRow {
  type: { id: string; name: string };
  form: {
    id: string;
    name: string;
    version: number;
    status: string;
    asked: number | null;
    questions: { key: string; label: string }[];
  } | null;
}
interface Answer {
  picker?: Picker;
  questions: Question[];
  forms: FormRow[];
  answerTypes: { value: string; label: string }[];
  savedOn: { value: string; label: string }[];
}

export async function intakeScreen(host: HTMLElement, ctx: ScreenContext) {
  const page = el("div");
  host.append(page);
  let data: Answer | null = null;

  async function load() {
    const res = await api<Answer>("GET", `/api/settings/intake${setupQuery("intake", "homecare")}`);
    if (!res.ok) {
      page.replaceChildren(
        el("p", { class: "note", role: "alert" }, "Intake questions couldn't be loaded."),
      );
      return;
    }
    data = res.body;
    draw();
  }

  function draw() {
    if (!data) return;
    const add = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add question");
    add.addEventListener("click", addQuestion);
    const asked = data.questions.filter((q) => q.askedOf.length && !q.archived);
    page.replaceChildren(
      pageHead(ctx, "Asked when a referral comes in.", [add], null),
      // Brief 1E-7: Home Care plus Shared to start, with the picker.
      setupBar("intake", "Intake questions", data.picker, () => void load()),
      table(
        [
          { h: "Question", f: (q) => el("b", {}, q.label) },
          { h: "Answer type", f: (q) => q.answerLabel },
          { h: "Asked of", f: (q) => q.askedOf.join(", ") },
          { h: "When", f: () => "At intake" },
          { h: "Saved on", f: (q) => q.savedOnLabel },
          { h: "Notes", f: (q) => (q.options.length ? q.options.join(" · ") : "") },
        ],
        asked,
        undefined,
        "Intake questions",
      ),
      el(
        "section",
        { class: "card", "aria-label": "Intake forms" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("file"), " Intake form per client type"),
            el(
              "span",
              { class: "small muted" },
              "An approved form is locked. A change makes the next version.",
            ),
          ),
        ),
        table(
          [
            { h: "Client type", f: (r) => el("b", {}, r.type.name) },
            { h: "Form", f: (r) => r.form?.name ?? "–" },
            {
              h: "Version",
              f: (r) =>
                r.form
                  ? el(
                      "span",
                      {},
                      `${r.form.version} `,
                      chip(
                        r.form.status === "approved" ? "Approved" : "Draft",
                        r.form.status === "approved" ? "ok" : "warn",
                      ),
                    )
                  : "–",
            },
            { h: "Asks", f: (r) => (r.form ? `${r.form.questions.length} questions` : "–") },
            {
              h: "",
              num: true,
              f: (r) => {
                if (!r.form) return "";
                const f = r.form;
                const change = ib("pencil", `Change ${f.name}'s questions`, () => changeForm(r));
                if (f.status === "approved") return change;
                const approve = el(
                  "button",
                  { type: "button", class: "btn sm" },
                  icon("check"),
                  " Approve",
                );
                approve.addEventListener("click", async () => {
                  approve.disabled = true;
                  const res = await api("POST", `/api/settings/forms/${f.id}/approve`, {});
                  approve.disabled = false;
                  if (!res.ok) return toast("That wasn't approved", res.body.error);
                  toast(`${f.name} approved`, `Version ${f.version} is locked; intake asks it now`);
                  await load();
                });
                return el("span", { class: "rowacts" }, change, approve);
              },
            },
          ],
          data.forms,
          undefined,
          "Intake form per client type",
        ),
      ),
    );
  }

  function addQuestion() {
    if (!data) return;
    const label = field("Question", "", { maxlength: "200" });
    const type = select(
      "Answer type",
      data.answerTypes.map((t) => [t.value, t.label] as const),
      "text",
    );
    const saved = select(
      "Saved on",
      data.savedOn.map((t) => [t.value, t.label] as const),
      "episode",
    );
    const options = field("Options (for a choice, separated by commas)", "", { maxlength: "400" });
    const types = pills(
      "Asked of",
      "checkbox",
      data.forms.map((f) => [f.type.id, f.type.name] as const),
      [],
      "Added to each type's intake form. An approved form gets a new draft version.",
    );
    const go = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add question");
    const m = openModal({
      title: "Add a question",
      sub: "It goes in the shared questions and on the intake forms picked below.",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el(
            "div",
            { class: "formgrid" },
            el("div", { class: "wide" }, label.el),
            type.el,
            saved.el,
            el("div", { class: "wide" }, options.el),
            types.el,
          ),
        ),
      ],
      actions: [go],
    });
    label.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const res = await api<Answer>(
        "POST",
        `/api/settings/intake/questions${setupQuery("intake", "homecare")}`,
        {
          label: label.input.value,
          answerType: type.input.value,
          savedOn: saved.input.value,
          options: options.input.value
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean),
          types: types.values(),
        },
      );
      go.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      data = res.body;
      draw();
      toast("Question added", "Approve each changed intake form to start asking it");
    });
  }

  function changeForm(r: FormRow) {
    if (!data || !r.form) return;
    const f = r.form;
    const had = new Set(f.questions.map((q) => q.key));
    const boxes = data.questions
      .filter((q) => !q.archived || had.has(q.key))
      .map((q) => ({
        q,
        box: el("input", { type: "checkbox", checked: had.has(q.key), id: `fq-${q.id}` }),
      }));
    const save = el("button", { type: "button", class: "btn pri" }, "Save");
    const m = openModal({
      title: `${f.name} · version ${f.version}`,
      sub:
        f.status === "approved"
          ? "Approved and locked: saving makes version " + (f.version + 1) + " as a draft."
          : "A draft: it changes in place.",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          ...boxes.map(({ q, box }) =>
            el(
              "label",
              { for: box.id, style: "display:flex;gap:8px;align-items:center;margin-bottom:4px" },
              box,
              q.label,
              el("span", { class: "small muted" }, ` · ${q.answerLabel} · ${q.savedOnLabel}`),
            ),
          ),
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      // Keep the order the form had, then the new ones in the bank's order.
      const keys = [
        ...f.questions
          .map((q) => q.key)
          .filter((k) => boxes.find((b) => b.q.key === k)?.box.checked),
        ...boxes.filter((b) => b.box.checked && !had.has(b.q.key)).map((b) => b.q.key),
      ];
      const res = await api("PUT", `/api/settings/forms/${f.id}`, { questionKeys: keys });
      save.disabled = false;
      if (!res.ok) return refuse(m.note, res.body.error);
      m.close();
      toast(
        "Form changed",
        f.status === "approved"
          ? `Version ${f.version + 1} is a draft until it's approved`
          : undefined,
      );
      await load();
    });
  }

  await load();
}
